import React from 'react';
import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import { createIntl, createIntlCache, IntlProvider } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  useLocation,
  useNavigate,
  useOutlet,
  useSearchParams,
} from 'react-router';
import {
  Button,
  ConfigProvider,
  Empty,
  Space,
  Typography,
  message,
} from 'antd';
import { getItem, useEffectWithPrev } from '@owl-frontend/components';
import Spinner from '../assets/app-spinner.svg';
import { WsConnectionBanner } from '../components/Common/WsConnectionBanner';
import ErrorContainer, {
  ErrorType,
} from '../components/ErrorContainer/ErrorContainer';
import { EscRouteDefs } from '../components/Routing/Routing';
import { actions as localizationSliceActions } from '../data/localization/logic';
import { actions as searchSliceActions } from '../data/search/logic';
import { actions as sessionSliceActions } from '../data/session/logic';
import type { StoreState } from '../data/store';
import { actions as wsSliceActions } from '../data/ws/logic';
import {
  type ConnectionState,
  platformWebSocketManager,
} from '../data/ws/WsManager';
import { EventBusProvider } from './event-bus';
import { getThemeConfig } from './theme';

interface Props extends React.PropsWithChildren {
  Router?: React.ComponentType;
}

export const Locale = React.createContext<{
  locale: 'en-us';
  messages: {
    [k: string]: string;
  };
  broadcastMessage(data: {
    key?: string;
    id: string;
    type?: 'info' | 'warning' | 'error' | 'loading' | 'success';
    values?: Record<string, any>;
  }): void;
}>({
  locale: 'en-us',
  messages: {},
  broadcastMessage: () => {
    throw new Error('Locale Context broadcastMessage not hooked up!');
  },
});

export const SessionContext = React.createContext<{
  current?: StoreState['session']['current'];
}>({});

const getCookieValue = (cookieName: string) => {
  return (
    document.cookie
      .split(';')
      .map((cookie) => cookie.split('=').map((part) => part.trim()))
      .find(([cookieKey]) => cookieKey === cookieName)?.[1] || null
  );
};

const ContextProvider: React.FC<Props> = () => {
  const outlet = useOutlet();
  const dispatch = useDispatch();
  const location = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [messageApi, messageContextHolder] = message.useMessage();
  const { locale, messages } = useSelector(
    (s: StoreState) => s['localization']
  );
  const intl = React.useMemo(() => {
    const cache = createIntlCache();
    return createIntl({ locale, messages }, cache);
  }, [locale, messages]);
  const {
    current,
    versionRefreshAvailable,
    darkMode,
    actions: {
      current: currentAction,
      requestToken: requestTokenAction,
      requestLogin: requestLoginAction,
      requestLogout: requestLogoutAction,
      pollRefreshToken: pollRefreshTokenAction,
    },
  } = useSelector((s: StoreState) => s['session']);

  const themeConfig = React.useMemo(() => getThemeConfig(darkMode), [darkMode]);

  React.useEffect(() => {
    // Render static Modal/message/notification calls inside the app theme.
    ConfigProvider.config({
      holderRender: (children) => (
        <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>
      ),
    });
  }, [themeConfig]);

  React.useEffect(() => {
    Cookies.set('darkMode', String(darkMode), {
      expires: 365,
      sameSite: 'lax',
    });
    datadogRum.setGlobalContextProperty('darkMode', darkMode);
  }, [darkMode]);

  React.useEffect(() => {
    platformWebSocketManager.setOnStateChange(
      (connectionState: ConnectionState) => {
        dispatch(wsSliceActions.setConnectionState(connectionState));
      }
    );
  }, [dispatch]);

  React.useEffect(() => {
    const authCode = params.get('code');
    const state = params.get('state');

    dispatch(sessionSliceActions.requestToken({ authCode, state }));
    dispatch(searchSliceActions.garbageCollect());
    // dispatch once on mount
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    dispatch(localizationSliceActions.fetchMessages({ locale }));
  }, [locale]);

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (requestTokenAction.status === 'fulfilled') {
        dispatch(sessionSliceActions.current());
        const url = new URL(window.location.href);
        // Clear authentication parameters from URL after successful token exchange
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        const newPath = url.pathname + url.search + url.hash;
        navigate(newPath, { replace: true });
      }

      if (requestTokenAction.status === 'rejected') {
        // param first in case we're swapping which tenant we log into
        const tenant =
          params.get('tenant') ?? getCookieValue('tenant') ?? getItem('tenant');
        if (tenant) {
          dispatch(
            sessionSliceActions.requestLogin({
              tenantId: tenant,
            })
          );
          return;
        }

        navigate(EscRouteDefs.Broken, {
          state: {
            error: ErrorType.MissingTenantId,
          },
        });
      }
    },
    [requestTokenAction.status, navigate, params]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (currentAction.status === 'fulfilled') {
        dispatch(wsSliceActions.initializeWebSocket());
        dispatch(sessionSliceActions.pollRefreshToken());

        if (location.pathname === EscRouteDefs.Broken) {
          navigate(EscRouteDefs.Home);
        }
      }

      if (currentAction.status === 'rejected') {
        dispatch(sessionSliceActions.requestLogout());
      }
    },
    [currentAction.status, location, navigate, params]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (currentAction.status === 'fulfilled') {
        dispatch(sessionSliceActions.pollVersion());
      }
    },
    [currentAction.status]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (requestLogoutAction.status === 'fulfilled') {
        dispatch(wsSliceActions.teardownWebSocket());
      }
    },
    [requestLogoutAction.status]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (pollRefreshTokenAction.status === 'rejected') {
        const tenant = getCookieValue('tenant') ?? getItem('tenant');
        if (tenant) {
          dispatch(sessionSliceActions.requestLogin({ tenantId: tenant }));
          return;
        }
        navigate(EscRouteDefs.Broken);
      }
    },
    [pollRefreshTokenAction.status, navigate]
  );

  React.useEffect(() => {
    const messageKey = 'platform-version-refresh';
    if (!versionRefreshAvailable) {
      messageApi.destroy(messageKey);
      return;
    }

    messageApi.open({
      key: messageKey,
      duration: 0,
      content: (
        <Space>
          {messages['platform.version_refresh.message']}
          <Button
            size="small"
            type="primary"
            onClick={() => {
              messageApi.destroy(messageKey);
              window.location.reload();
            }}
          >
            {messages['platform.version_refresh.button']}
          </Button>
        </Space>
      ),
    });
  }, [messageApi, versionRefreshAvailable, messages]);

  if (
    requestTokenAction.status === 'pending' ||
    requestLoginAction.status === 'pending' ||
    currentAction.status === 'pending' ||
    requestTokenAction.status === 'uninitialized' ||
    (requestTokenAction.status === 'fulfilled' &&
      !current &&
      currentAction.status !== 'rejected')
  ) {
    return (
      <>
        <WsConnectionBanner />
        <div className="app-spinner">
          <Spinner />
        </div>
      </>
    );
  }

  if (requestLogoutAction.status === 'pending') {
    return (
      <>
        <WsConnectionBanner />
        <div className="app-spinner">
          <Spinner />
        </div>
        <Typography.Text type="secondary">
          {messages['platform.start_up.logout']}
        </Typography.Text>
      </>
    );
  }

  if (!current) {
    return (
      <>
        <WsConnectionBanner />
        <ErrorContainer />
      </>
    );
  }

  return (
    <IntlProvider
      locale={locale}
      defaultLocale="en"
      messages={messages}
      onError={() => {
        /// noop
      }}
    >
      <Locale.Provider
        value={{
          locale,
          messages,
          broadcastMessage: (data) => {
            if (!data.id) {
              return;
            }

            const content = intl.formatMessage(
              { id: data.id },
              data.values || {}
            );
            messageApi.destroy(data.key ?? data.id);
            messageApi[data.type ?? 'info']({
              key: data.key ?? data.id,
              content,
            });
          },
        }}
      >
        <ConfigProvider
          theme={themeConfig}
          empty={{ image: Empty.PRESENTED_IMAGE_SIMPLE }}
        >
          <EventBusProvider>
            <SessionContext.Provider value={{ current }}>
              <WsConnectionBanner />
              {outlet}
              {messageContextHolder}
            </SessionContext.Provider>
          </EventBusProvider>
        </ConfigProvider>
      </Locale.Provider>
    </IntlProvider>
  );
};
export default ContextProvider;
